/**
 * Le risposte della porta d'accesso del backend (/public/accesso/entra, /rinnova), lette e basta.
 *
 * Dal novembre 2026 il browser e l'app non parlano piu' con Keycloak: entrare, rinnovare e uscire
 * passano dal backend, che chiede a Keycloak per noi (AccessoKeycloak). Qui c'e' come si leggono le
 * sue risposte, separato da keycloak.js perche' sono regole che decidono se la persona esce, e
 * vanno provate da sole: funzioni pure, senza import, che una prova con node carica cosi' come sono.
 */

/** Il codice d'errore del backend e la chiave della lingua con cui dirlo a chi sta entrando. */
const MESSAGGI_ACCESSO = {
  CREDENZIALI_NON_VALIDE: 't-accesso-credenziali-non-valide',
  CODICE_RICHIESTO: 't-2fa-serve-codice-app',
  ACCOUNT_BLOCCATO: 't-accesso-account-bloccato',
  TROPPI_TENTATIVI: 't-accesso-troppi-tentativi',
  ACCOUNT_DA_COMPLETARE: 't-accesso-account-da-completare',
  SERVIZIO_NON_DISPONIBILE: 't-accesso-servizio-non-disponibile',
};

/**
 * I gettoni di una risposta riuscita, o null se mancano: una pagina che non e' la nostra (il
 * portale di un Wi-Fi pubblico risponde 200 con il suo HTML) non deve sembrare un accesso.
 * @returns {{accessToken: string, refreshToken: string, idToken?: string}|null}
 */
export function gettoniDa(corpo) {
  const dati = corpo && corpo.data;
  return dati && dati.accessToken && dati.refreshToken ? dati : null;
}

/**
 * Perche' l'accesso non e' riuscito, e con quale chiave della lingua dirlo.
 * @param {number} stato lo stato HTTP della risposta
 * @param {Object|null} corpo il JSON della risposta, se c'era
 * @returns {{codice: string|null, chiave: string}}
 */
export function erroreDiAccesso(stato, corpo) {
  const codice = (corpo && corpo.error && corpo.error.code) || null;
  if (codice && MESSAGGI_ACCESSO[codice]) return { codice, chiave: MESSAGGI_ACCESSO[codice] };
  return { codice, chiave: stato >= 500 ? 't-accesso-servizio-non-disponibile' : 't-login-failed' };
}

/**
 * L'esito di un rinnovo, con le stesse tre risposte di quando lo si chiedeva a Keycloak (vedi
 * chiediRinnovo in keycloak.js):
 *   'ok'     i gettoni nuovi sono in `gettoni`;
 *   'finita' SOLO quando il backend dice SESSIONE_FINITA, cioe' Keycloak ha risposto invalid_grant;
 *   'rete'   tutto il resto: un 5xx, il bilanciatore, una risposta che non e' nostra. La sessione
 *            probabilmente c'e' ancora, e nessuno deve uscire. `ripetibile` falso quando il
 *            backend ha dato un errore suo che ripetuto non cambia (un client non configurato):
 *            riprovarlo dopo 1, 2 e 4 secondi farebbe solo aspettare.
 * Il dettaglio finisce nel resoconto dell'uscita: mai il gettone.
 * @returns {{esito: 'ok'|'rete'|'finita', dettaglio?: string|null, ripetibile?: boolean, gettoni?: Object}}
 */
export function esitoDelRinnovo(stato, corpo) {
  if (stato >= 200 && stato < 300) {
    const gettoni = gettoniDa(corpo);
    return gettoni ? { esito: 'ok', dettaglio: null, gettoni } : { esito: 'rete', dettaglio: 'risposta senza token' };
  }
  const codice = (corpo && corpo.error && corpo.error.code) || '';
  if (codice === 'SESSIONE_FINITA') {
    // Lo stesso testo di quando rispondeva Keycloak ("invalid_grant: Session not active"): il
    // resoconto delle uscite si legge allo stesso modo prima e dopo.
    const descrizione = String((corpo.error && corpo.error.message) || '').slice(0, 120);
    return { esito: 'finita', dettaglio: `invalid_grant: ${descrizione}` };
  }
  if (stato >= 500 && codice !== 'ACCESSO_NON_CONFIGURATO') {
    return { esito: 'rete', dettaglio: `http ${stato}` };
  }
  const ripetibile = !(codice && stato !== 408 && stato !== 429);
  return { esito: 'rete', dettaglio: `http ${stato} ${codice}`.trim(), ripetibile };
}
